'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useMemo } from 'react';
import type { SessionAction } from './session-action';
import type { SessionTurnState } from './session-turn-state';
import type { ForkableMessage } from './transcript-fork';
import { blockedAction, decidedAction } from './session-action';
import { REWINDING_LABEL } from './session-turn-state';
import { forkableMessages, forkDialogReason, latestFork, messageForks } from './transcript-fork';
import { useForkSession } from './use-fork-session';
import { usePendingForks } from '@/data/code-sessions/mutations';
import { FORK_BLOCKED_REASON } from '@/lib/code-sessions/fork-policy';
import { deterministicPromptId } from '@/lib/code-sessions/turn-utils';
export const FORKING_LABEL = 'Forking session';

/** The Fork from message dialog for one session. */
export type ForkFromMessage = {
  /** Why no message can be picked; an empty conversation still opens the dialog. */
  disabledReason?: string;
  /** Prompts the dialog offers, newest first. */
  messages: readonly ForkableMessage[];
  /** Fork before the message's turn, with its prompt in the fork's composer. */
  run: (turnId: string) => void;
};
export type SessionFork = {
  /** Fork from here on each prompt bubble, keyed by bubble ID. */
  forks: ReadonlyMap<string, SessionAction>;
  /** Fork the whole conversation. */
  latest: SessionAction;
  fromMessage: ForkFromMessage;
};
type ForkModel = {
  forks: ReadonlyMap<string, SessionAction>;
  messages: readonly ForkableMessage[];
};
const NO_FORKS: ForkModel = {
  forks: new Map(),
  messages: []
};

/**
 * Fork from here, Fork from latest message, and the Fork from message dialog
 * for one session, under the same policy the server enforces.
 */
export function useSessionFork(sessionId, state) {
  const $ = _c(31);
  const forkSession = useForkSession();
  const t0 = usePendingForks();
  let t1;
  if ($[0] !== sessionId || $[1] !== t0) {
    let t2;
    if ($[3] !== sessionId) {
      t2 = fork => fork.sessionId === sessionId;
      $[3] = sessionId;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = t0.find(t2);
    $[0] = sessionId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const pendingFork = t1;
  const forking = pendingFork !== undefined;
  const forkingTurnId = pendingFork?.beforeTurnId;
  const busyReason = state?.rewinding ? REWINDING_LABEL : forking ? FORKING_LABEL : undefined;
  const drafts = state?.drafts;
  let t2;
  if ($[5] !== drafts || $[6] !== forkSession || $[7] !== sessionId) {
    t2 = turnId => {
      if (drafts === undefined) {
        return;
      }
      forkSession({
        sessionId,
        beforeTurnId: turnId,
        draft: turnId === undefined ? undefined : drafts.get(turnId)
      });
    };
    $[5] = drafts;
    $[6] = forkSession;
    $[7] = sessionId;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const forkFrom = t2;
  let t3;
  if ($[9] !== busyReason || $[10] !== state) {
    t3 = state === null ? {
      ok: false as const,
      reason: FORK_BLOCKED_REASON.STARTING
    } : latestFork(state, busyReason);
    $[9] = busyReason;
    $[10] = state;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const latestDecision = t3;
  const latestReason = latestDecision.ok ? undefined : latestDecision.reason;
  let t4;
  if ($[12] !== forkFrom || $[13] !== latestReason) {
    t4 = latestReason === undefined ? {
      run: () => forkFrom()
    } : blockedAction(latestReason);
    $[12] = forkFrom;
    $[13] = latestReason;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const latest = t4;
  let t5;
  if ($[15] !== busyReason || $[16] !== state) {
    t5 = state === null ? FORK_BLOCKED_REASON.STARTING : forkDialogReason(state, busyReason);
    $[15] = busyReason;
    $[16] = state;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const dialogReason = t5;
  let t6;
  if ($[18] !== busyReason || $[19] !== forkFrom || $[20] !== forkingTurnId || $[21] !== state) {
    t6 = state === null ? NO_FORKS : {
      forks: new Map([...messageForks(state, busyReason)].map(t7 => {
        const [turnId_0, decision] = t7;
        return [deterministicPromptId(turnId_0), turnId_0 === forkingTurnId ? {
          ...blockedAction(FORKING_LABEL),
          pending: true as const
        } : decidedAction(decision, () => forkFrom(turnId_0))];
      })),
      messages: forkableMessages(state)
    };
    $[18] = busyReason;
    $[19] = forkFrom;
    $[20] = forkingTurnId;
    $[21] = state;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  const model = t6;
  let t7;
  if ($[23] !== dialogReason || $[24] !== forkFrom || $[25] !== model.messages) {
    t7 = {
      disabledReason: dialogReason,
      messages: model.messages,
      run: forkFrom
    };
    $[23] = dialogReason;
    $[24] = forkFrom;
    $[25] = model.messages;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  const fromMessage = t7;
  let t8;
  if ($[27] !== fromMessage || $[28] !== latest || $[29] !== model.forks) {
    t8 = {
      forks: model.forks,
      latest,
      fromMessage
    };
    $[27] = fromMessage;
    $[28] = latest;
    $[29] = model.forks;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  return t8;
}